/**
 * Track Result Page — GE Lines
 *
 * Fetches public tracking info for a given reference number.
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_BASE } from '../../lib/api-base';
import { ArrowLeft, Package, CheckCircle2, Clock, AlertCircle, MapPin } from 'lucide-react';
import { useStorefront } from '@/context/StorefrontContext';
import { useSurfaceAccess } from '@/context/SurfaceAccessContext';

interface TrackingEvent {
  date: string;
  description: string;
  location: string;
  status: 'completed' | 'active' | 'upcoming';
}

interface TrackingResult {
  reference: string;
  blNo?: string;
  container?: string;
  origin: string;
  destination: string;
  eta: string;
  status: string;
  type?: string;
  shipper?: string;
  consignee?: string;
  vessel?: string;
  voyage?: string;
  events: TrackingEvent[];
}

interface TrackingAccessAction {
  url: string;
  method: 'GET' | 'POST';
  target?: '_blank' | '_self';
  fields?: Record<string, string>;
}

interface TrackingAccessOption {
  provider: string;
  mode: string;
  label: string;
  referenceType: 'sea-container' | 'sea-bill-of-lading' | 'air-awb';
  referenceValue: string;
  isPreferred: boolean;
  canNormalizeMilestones: boolean;
  requiresCommercialSetup?: boolean;
  carrierScac?: string;
  carrierName?: string;
  action?: TrackingAccessAction;
  warnings?: string[];
}

interface TrackingAccessRecommendation {
  strategy: 'canonical-first' | 'external-first';
  reason: string;
  agentReply: string;
  option: TrackingAccessOption;
}

interface TrackingAccessResult {
  referenceType: 'sea-container' | 'sea-bill-of-lading' | 'air-awb';
  referenceValue: string;
  inferredReferenceType: boolean;
  carrierScac?: string;
  carrierName?: string;
  recommendation: TrackingAccessRecommendation;
  options: TrackingAccessOption[];
  warnings: string[];
}

function mapApiTrack(raw: Record<string, unknown>): TrackingResult {
  const formatDate = (d: unknown) =>
    d ? new Date(d as string).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return {
    reference: String(raw.jobNo ?? raw.refNo ?? raw.hawb ?? ''),
    blNo: raw.hblNo ? String(raw.hblNo) : raw.hawb ? String(raw.hawb) : undefined,
    container: raw.containerNo ? String(raw.containerNo) : undefined,
    origin: String(raw.origin ?? ''),
    destination: String(raw.pod ?? raw.destination ?? ''),
    eta: formatDate(raw.eta),
    status: String(raw.status ?? 'active').toLowerCase(),
    type: String(raw.type ?? ''),
    shipper: raw.shipper ? String(raw.shipper) : undefined,
    consignee: raw.consignee ? String(raw.consignee) : undefined,
    vessel: raw.vessel ? String(raw.vessel) : undefined,
    voyage: raw.voyage ? String(raw.voyage) : undefined,
    events: [],
  };
}

const STATUS_COLORS: Record<string, string> = {
  delivered:  'bg-emerald-100 text-emerald-700',
  in_transit: 'bg-blue-100 text-blue-700',
  at_port:    'bg-sky-100 text-sky-700',
  customs:    'bg-amber-100 text-amber-700',
  booked:     'bg-slate-100 text-slate-600',
  external_tracking: 'bg-violet-100 text-violet-700',
};

function formatReferenceType(referenceType: TrackingAccessResult['referenceType']): string {
  switch (referenceType) {
    case 'sea-container':
      return 'Container';
    case 'sea-bill-of-lading':
      return 'Bill of Lading';
    case 'air-awb':
      return 'Air Waybill';
  }
}

function buildFallbackResult(referenceValue: string, trackingAccess: TrackingAccessResult): TrackingResult {
  return {
    reference: referenceValue,
    blNo: trackingAccess.referenceType === 'sea-bill-of-lading' ? trackingAccess.referenceValue : undefined,
    container: trackingAccess.referenceType === 'sea-container' ? trackingAccess.referenceValue : undefined,
    origin: '',
    destination: '',
    eta: '—',
    status: 'external_tracking',
    type: trackingAccess.referenceType,
    vessel: undefined,
    voyage: undefined,
    shipper: undefined,
    consignee: undefined,
    events: [],
  };
}

function openTrackingAction(action: TrackingAccessAction): void {
  if (action.method === 'GET') {
    window.open(action.url, action.target ?? '_blank', 'noopener,noreferrer');
    return;
  }

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action.url;
  form.target = action.target ?? '_blank';
  form.style.display = 'none';

  for (const [key, value] of Object.entries(action.fields ?? {})) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
  form.remove();
}

export function TrackResultPage() {
  const { reference } = useParams<{ reference: string }>();
  const { host, brand } = useStorefront();
  const { ensureAccess, buildHeaders } = useSurfaceAccess();
  const [result, setResult] = useState<TrackingResult | null>(null);
  const [trackingAccess, setTrackingAccess] = useState<TrackingAccessResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!reference) return;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      const allowed = await ensureAccess('tracking');
      if (!allowed || cancelled) {
        setLoading(false);
        return;
      }

      try {
        const headers = buildHeaders();
        const [legacyResponse, accessResponse] = await Promise.allSettled([
          fetch(`${API_BASE}/api/selfservice/track/${encodeURIComponent(reference)}?host=${encodeURIComponent(host)}`, {
            headers,
          }),
          fetch(`${API_BASE}/api/selfservice/track/${encodeURIComponent(reference)}/foundation?host=${encodeURIComponent(host)}`, {
            headers,
          }),
        ]);

        let nextResult: TrackingResult | null = null;
        let nextTrackingAccess: TrackingAccessResult | null = null;
        let legacyError: string | null = null;
        let accessError: string | null = null;

        if (legacyResponse.status === 'fulfilled') {
          const response = legacyResponse.value;
          if (response.status === 401) {
            legacyError = 'Please verify your email to continue tracking';
          } else if (response.status === 404) {
            legacyError = 'Shipment not found in portal records';
          } else if (!response.ok) {
            legacyError = 'Tracking unavailable';
          } else {
            const json = await response.json() as { ok: boolean; data: Record<string, unknown> };
            nextResult = mapApiTrack(json.data);
          }
        } else {
          legacyError = 'Tracking unavailable';
        }

        if (accessResponse.status === 'fulfilled') {
          const response = accessResponse.value;
          if (response.ok) {
            const json = await response.json() as { ok: boolean; data: TrackingAccessResult };
            nextTrackingAccess = json.data;
          } else if (response.status !== 400 && response.status !== 404) {
            accessError = response.status === 401
              ? 'Please verify your email to continue tracking'
              : 'External tracking access is unavailable';
          }
        } else {
          accessError = 'External tracking access is unavailable';
        }

        if (!nextResult && nextTrackingAccess) {
          nextResult = buildFallbackResult(reference, nextTrackingAccess);
        }

        if (!nextResult) {
          throw new Error(accessError ?? legacyError ?? 'Tracking unavailable');
        }

        if (!cancelled) {
          setResult(nextResult);
          setTrackingAccess(nextTrackingAccess);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching tracking');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [reference, host, ensureAccess, buildHeaders]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-12 animate-fade-in">
      <Link to="/track" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-600 mb-8 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to search
      </Link>

      {loading && (
        <div className="bg-white rounded-2xl p-12 shadow text-center">
          <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Looking up {reference}…</p>
        </div>
      )}

      {error && (
        <div className="bg-white rounded-2xl p-8 shadow border border-red-100 flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
          <div>
            <h2 className="font-semibold text-slate-900 mb-1">{error}</h2>
            <p className="text-sm text-slate-500">
              Please check your reference number or{' '}
              <Link to="/contact" className="text-brand-600 hover:underline">contact {brand.brandShortName}</Link>.
            </p>
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-6">
          {/* Summary card */}
          <div className="bg-white rounded-2xl shadow p-6 border border-slate-100">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center">
                  <Package className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-slate-900">{result.reference}</p>
                  {result.blNo && <p className="text-sm text-slate-500">B/L: {result.blNo}</p>}
                  {result.container && <p className="text-sm text-slate-500">CTR: {result.container}</p>}
                  {result.type === 'air-awb' && <p className="text-sm text-slate-500">AWB: {result.reference}</p>}
                </div>
              </div>
              <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${STATUS_COLORS[result.status] ?? 'bg-slate-100 text-slate-600'}`}>
                {result.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Origin</p>
                <p className="font-medium text-slate-800">{result.origin || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Destination</p>
                <p className="font-medium text-slate-800">{result.destination || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-0.5">ETA</p>
                <p className="font-medium text-slate-800">{result.eta}</p>
              </div>
              {result.vessel && (
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">Vessel</p>
                  <p className="font-medium text-slate-800">{result.vessel}{result.voyage ? ` / ${result.voyage}` : ''}</p>
                </div>
              )}
              {result.shipper && (
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">Shipper</p>
                  <p className="font-medium text-slate-800 truncate">{result.shipper}</p>
                </div>
              )}
              {result.consignee && (
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">Consignee</p>
                  <p className="font-medium text-slate-800 truncate">{result.consignee}</p>
                </div>
              )}
            </div>
          </div>

          {trackingAccess && (
            <div className="bg-white rounded-2xl shadow p-6 border border-slate-100">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-semibold text-slate-900">Tracking Access</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {formatReferenceType(trackingAccess.referenceType)} detected for {trackingAccess.referenceValue}
                  </p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-brand-50 text-brand-700">
                  {trackingAccess.recommendation.strategy === 'canonical-first' ? 'Boox first' : 'External first'}
                </span>
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-900">Recommended next step</p>
                <p className="text-sm text-slate-600 mt-1">{trackingAccess.recommendation.reason}</p>
                <p className="text-xs text-slate-500 mt-2">{trackingAccess.recommendation.agentReply}</p>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {trackingAccess.options.map((option) => (
                  <div key={`${option.provider}-${option.referenceValue}`} className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{option.label}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {option.mode}{option.carrierName ? ` · ${option.carrierName}` : ''}
                        </p>
                      </div>
                      {option.isPreferred && (
                        <span className="px-2 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700">
                          Recommended
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-slate-500 mt-3">
                      {option.canNormalizeMilestones
                        ? 'Normalized Boox milestone flow'
                        : 'External tracking experience'}
                    </p>

                    {option.action ? (
                      <button
                        type="button"
                        onClick={() => openTrackingAction(option.action!)}
                        className="mt-3 inline-flex items-center justify-center rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white hover:bg-brand-800 transition-colors"
                      >
                        {option.mode === 'form-post' ? 'Open tracking' : 'Open provider'}
                      </button>
                    ) : (
                      <p className="mt-3 text-xs text-slate-500">
                        This path is available inside the Boox workspace rather than as a public outbound link.
                      </p>
                    )}

                    {option.warnings && option.warnings.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {option.warnings.map((warning) => (
                          <p key={warning} className="text-[11px] text-amber-700 bg-amber-50 rounded-md px-2 py-1">
                            {warning}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {trackingAccess.warnings.length > 0 && (
                <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50 p-4 space-y-1">
                  {trackingAccess.warnings.map((warning) => (
                    <p key={warning} className="text-xs text-amber-800">{warning}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Timeline */}
          <div className="bg-white rounded-2xl shadow p-6 border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-5">Shipment Timeline</h3>
            {result.events.length > 0 ? (
              <ol className="relative border-l border-slate-200 space-y-6 pl-6">
                {result.events.map((ev, i) => (
                  <li key={i} className="relative">
                    <span className={`absolute -left-[25px] flex items-center justify-center w-5 h-5 rounded-full ${
                      ev.status === 'completed' ? 'bg-emerald-100' :
                      ev.status === 'active'    ? 'bg-brand-100' : 'bg-slate-100'
                    }`}>
                      {ev.status === 'completed' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
                      {ev.status === 'active'    && <Clock className="w-3.5 h-3.5 text-brand-600 animate-pulse" />}
                      {ev.status === 'upcoming'  && <span className="w-2 h-2 rounded-full bg-slate-300" />}
                    </span>
                    <p className={`text-sm font-medium ${ev.status === 'upcoming' ? 'text-slate-400' : 'text-slate-900'}`}>
                      {ev.description}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                      <p className="text-xs text-slate-400">{ev.location} · {ev.date}</p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-slate-500">
                No normalized milestone timeline is available on this public page yet. Use the tracking access options above for live carrier-side tracking.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
